import { Quaternion, Vector3 } from 'three';

const ZERO = Object.freeze({ x: 0, y: 0, z: 0 });

/**
 * Thin, ergonomic wrapper around an Ammo rigid body.
 *
 * The wrapper owns the mesh association and a small pool of native temporaries.
 * `Physics` owns adding/removing the native body from the world.
 */
export class RigidBody {
  constructor(physics, { native, motionState, shape, auxiliaryShapes = [], mesh = null, userData, group, mask, mass, kinematic = false }) {
    this.physics = physics;
    this.native = native;
    this.motionState = motionState;
    this.shape = shape;
    this.auxiliaryShapes = auxiliaryShapes;
    this.mesh = mesh;
    this.userData = userData ?? {};
    this.group = group;
    this.mask = mask;
    this.mass = mass;
    this.kinematic = kinematic;
    this.disposed = false;

    const Ammo = physics.Ammo;
    this._transform = new Ammo.btTransform();
    this._vecA = new Ammo.btVector3(0, 0, 0);
    this._vecB = new Ammo.btVector3(0, 0, 0);
    this._quat = new Ammo.btQuaternion(0, 0, 0, 1);

    this.sync();
  }

  get isStatic() {
    return this.mass === 0;
  }

  getPosition(target = new Vector3()) {
    if (this.disposed) return target;
    this.motionState.getWorldTransform(this._transform);
    const origin = this._transform.getOrigin();
    return target.set(origin.x(), origin.y(), origin.z());
  }

  getRotation(target = new Quaternion()) {
    if (this.disposed) return target;
    this.motionState.getWorldTransform(this._transform);
    const rotation = this._transform.getRotation();
    return target.set(rotation.x(), rotation.y(), rotation.z(), rotation.w());
  }

  getLinearVelocity(target = new Vector3()) {
    if (this.disposed) return target;
    const velocity = this.native.getLinearVelocity();
    return target.set(velocity.x(), velocity.y(), velocity.z());
  }

  setLinearVelocity(velocity) {
    if (this.disposed) return;
    this._vecA.setValue(velocity.x, velocity.y, velocity.z);
    this.native.setLinearVelocity(this._vecA);
  }

  getAngularVelocity(target = new Vector3()) {
    if (this.disposed) return target;
    const velocity = this.native.getAngularVelocity();
    return target.set(velocity.x(), velocity.y(), velocity.z());
  }

  setAngularVelocity(velocity) {
    if (this.disposed) return;
    this._vecA.setValue(velocity.x, velocity.y, velocity.z);
    this.native.setAngularVelocity(this._vecA);
  }

  applyImpulse(impulse, relativePosition = ZERO) {
    if (this.disposed) return;
    this._vecA.setValue(impulse.x, impulse.y, impulse.z);
    this._vecB.setValue(relativePosition.x, relativePosition.y, relativePosition.z);
    this.native.applyImpulse(this._vecA, this._vecB);
    this.native.activate(true);
  }

  applyCentralImpulse(impulse) {
    if (this.disposed) return;
    this._vecA.setValue(impulse.x, impulse.y, impulse.z);
    this.native.applyCentralImpulse(this._vecA);
    this.native.activate(true);
  }

  activate() {
    if (!this.disposed) this.native.activate(true);
  }

  /** Move a body instantly and clear its velocities. Useful for respawns. */
  teleport(position, rotation = null) {
    if (this.disposed) return;

    this._transform.setIdentity();
    this._vecA.setValue(position.x, position.y, position.z);
    this._transform.setOrigin(this._vecA);

    if (rotation) {
      this._quat.setValue(rotation.x, rotation.y, rotation.z, rotation.w);
      this._transform.setRotation(this._quat);
    }

    this.native.setWorldTransform(this._transform);
    this.motionState.setWorldTransform(this._transform);

    this._vecA.setValue(0, 0, 0);
    this.native.setLinearVelocity(this._vecA);
    this.native.setAngularVelocity(this._vecA);
    this.native.activate(true);

    this.sync();
  }

  /** Copy the latest simulated transform onto the Three.js mesh. */
  sync() {
    if (this.disposed || !this.mesh) return;

    this.motionState.getWorldTransform(this._transform);
    const origin = this._transform.getOrigin();
    const rotation = this._transform.getRotation();

    this.mesh.position.set(origin.x(), origin.y(), origin.z());
    this.mesh.quaternion.set(rotation.x(), rotation.y(), rotation.z(), rotation.w());
  }

  /** Release per-body native temporaries. Physics destroys the body itself. */
  _release() {
    if (this.disposed) return;
    const Ammo = this.physics.Ammo;
    Ammo.destroy(this._transform);
    Ammo.destroy(this._vecA);
    Ammo.destroy(this._vecB);
    Ammo.destroy(this._quat);
    this.disposed = true;
    this.native = null;
    this.motionState = null;
    this.shape = null;
    this.auxiliaryShapes = [];
  }
}
